import React from "react"
import { ColumnsType } from "antd/es/table"
import { Button, Dropdown, MenuProps } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArchive,
  faRotateLeft,
  faFolder,
  faEllipsisVertical,
  faPenToSquare,
  faTrash,
  faFolderTree,
} from "@fortawesome/free-solid-svg-icons"
import { ProjectData, ProjectMetadata } from "types/project.types"
import { ProjectTreeRow } from "utils/projectTree"
import { User } from "stores/userStore"

interface FolderActions {
  onRenameFolder?: (folderPath: string) => void
  onDeleteFolder?: (folderPath: string, count: number) => void
  onMoveProject?: (project: ProjectData) => void
}

const isFolderRow = (
  record: ProjectTreeRow
): record is Extract<ProjectTreeRow, { kind: "folder" }> =>
  record.kind === "folder"

const getColumns = (
  isArchived: boolean,
  handleUnarchive: (projectId: string, owner: string) => Promise<void> | void,
  handleArchive: (projectId: string, owner: string) => Promise<void> | void,
  user: User,
  folderActions: FolderActions = {}
): ColumnsType<ProjectTreeRow> => [
  {
    title: "Project Name",
    key: 1,
    render: (_value, record) => {
      if (isFolderRow(record)) {
        return (
          <span className="font-semibold">
            <FontAwesomeIcon icon={faFolder} className="mr-2 text-[var(--dq-brand)]" />
            {record.folderName}
            <span className="ml-2 text-gray-500 font-normal">
              ({record.count})
            </span>
          </span>
        )
      }
      return record.metadata?.Name ?? ""
    },
    sorter: (a, b) => {
      // Folders always sort above projects, then alphabetical within each group.
      if (isFolderRow(a) && !isFolderRow(b)) return -1
      if (!isFolderRow(a) && isFolderRow(b)) return 1
      const aName = isFolderRow(a) ? a.folderName : a.metadata?.Name ?? ""
      const bName = isFolderRow(b) ? b.folderName : b.metadata?.Name ?? ""
      return aName.localeCompare(bName)
    },
    sortDirections: ["ascend", "descend"],
  },
  {
    title: "Type",
    key: 2,
    render: (_value, record) => {
      if (isFolderRow(record)) return ""
      const metadata = record.metadata as ProjectMetadata | undefined
      return metadata?.Type?.toUpperCase() ?? ""
    },
  },
  {
    title: "Project Owner",
    key: 3,
    render: (_value, record) => (isFolderRow(record) ? "" : record.owner),
  },
  {
    title: "Date Created",
    key: 4,
    render: (_value, record) =>
      isFolderRow(record) ? "" : record.metadata?.Created_at ?? "",
    sorter: (a, b) => {
      // Don't reorder folder rows by date; only project rows compare meaningfully.
      if (isFolderRow(a) || isFolderRow(b)) return 0
      return (
        new Date(a.metadata?.Created_at ?? 0).getTime() -
        new Date(b.metadata?.Created_at ?? 0).getTime()
      )
    },
    sortDirections: ["ascend"],
  },
  {
    title: "Status",
    key: 5,
    render: (_value, record) =>
      isFolderRow(record) ? "" : record.metadata?.Status ?? "",
  },
  {
    title: "Actions",
    key: 6,
    render: (_value, record) => {
      if (isFolderRow(record)) {
        const ownsAllInside =
          record.ownerOfFolderProjects === null
            ? record.count === 0 // empty folder → only the caller's view shows it, so allow
            : record.ownerOfFolderProjects.toLowerCase() ===
              user.username.toLowerCase()

        if (!ownsAllInside) return null

        const items: MenuProps["items"] = [
          {
            key: "rename",
            icon: <FontAwesomeIcon icon={faPenToSquare} />,
            label: "Rename folder",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation()
              folderActions.onRenameFolder?.(record.folderPath)
            },
          },
          {
            key: "delete",
            icon: <FontAwesomeIcon icon={faTrash} />,
            label: "Delete folder",
            danger: true,
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation()
              folderActions.onDeleteFolder?.(record.folderPath, record.count)
            },
          },
        ]
        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <Button
              aria-label="Folder actions"
              icon={<FontAwesomeIcon icon={faEllipsisVertical} />}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation()
              }}
            />
          </Dropdown>
        )
      }

      const isOwner =
        user.username.toLowerCase() === record.owner.toLowerCase()
      if (!isOwner) return null

      // Row actions are the small outlined buttons; none is solid.
      const archiveButton = (
        <Button
          icon={<FontAwesomeIcon icon={isArchived ? faRotateLeft : faArchive} />}
          iconPosition="end"
          onClick={(e: React.MouseEvent<HTMLElement>): void => {
            e.stopPropagation()
            if (isArchived) {
              handleUnarchive(record.id, record.owner)
            } else {
              handleArchive(record.id, record.owner)
            }
          }}
        >
          {isArchived ? "Unarchive" : "Archive"}
        </Button>
      )

      if (isArchived) return archiveButton

      return (
        <span className="inline-flex gap-2">
          <Button
            icon={<FontAwesomeIcon icon={faFolderTree} />}
            iconPosition="end"
            onClick={(e: React.MouseEvent<HTMLElement>): void => {
              e.stopPropagation()
              folderActions.onMoveProject?.(record)
            }}
          >
            Move
          </Button>
          {archiveButton}
        </span>
      )
    },
  },
]

export default getColumns
